"use client";

import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type InstallPromptCopyProps = {
  instructionText: string;
  prompt: string;
  copyLabel: string;
  downloadHref?: string;
  downloadLabel?: string;
};

export default function InstallPromptCopy({
  instructionText,
  prompt,
  copyLabel,
  downloadHref,
  downloadLabel,
}: InstallPromptCopyProps) {
  const [copied, setCopied] = useState(false);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="w-full space-y-2">
      <div className="flex justify-end gap-2">
        {downloadHref && (
          <Link
            href={downloadHref}
            className="inline-flex h-8 items-center rounded-full border border-cyan-200/28 bg-[linear-gradient(140deg,rgba(40,114,182,0.22),rgba(21,34,63,0.7))] px-3 text-xs font-semibold tracking-[0.01em] text-cyan-100 transition-all duration-200 hover:border-cyan-200/55 hover:bg-[linear-gradient(140deg,rgba(67,164,255,0.28),rgba(30,46,83,0.86))] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/65"
          >
            {downloadLabel ?? "Download"}
          </Link>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            "h-8 rounded-full border px-3 text-xs font-medium transition-all duration-200 focus-visible:ring-2 focus-visible:ring-cyan-300/65",
            copied
              ? "border-emerald-300/45 bg-emerald-500/18 text-emerald-100 hover:bg-emerald-500/22"
              : "border-cyan-200/25 bg-white/[0.04] text-slate-100 hover:border-cyan-200/50 hover:bg-cyan-300/12"
          )}
          onClick={copyPrompt}
        >
          {copied ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
          {copyLabel}
        </Button>
      </div>

      <div className="group relative isolate w-full max-w-[320px] overflow-hidden rounded-xl border border-cyan-200/15 bg-[linear-gradient(160deg,rgba(8,18,34,0.82),rgba(8,12,24,0.88))] p-3 shadow-[0_10px_34px_rgba(0,0,0,0.42)] transition-all duration-200 hover:border-cyan-200/35 hover:shadow-[0_18px_44px_rgba(8,133,222,0.18)] motion-reduce:transition-none lg:max-w-none">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-[radial-gradient(80%_120%_at_50%_0%,rgba(67,208,255,0.2),transparent_75%)]"
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -left-28 top-0 h-full w-24 -translate-x-full skew-x-[-24deg] bg-gradient-to-r from-transparent via-cyan-200/18 to-transparent opacity-0 transition-all duration-700 group-hover:translate-x-[560%] group-hover:opacity-100 motion-reduce:hidden"
        />

        <div className="relative space-y-2">
          <p className="text-[11px] leading-relaxed text-slate-300/85">{instructionText}</p>
          <code className="block whitespace-pre-wrap break-words rounded-md border border-white/10 bg-black/45 px-2 py-1.5 text-[12px] leading-5 text-slate-100">
            {prompt}
          </code>
        </div>
      </div>
    </div>
  );
}
